<template>
  <div class="logger-config">
    <!-- Global Enable Toggle -->
    <div class="logger-section">
      <label class="logger-toggle">
        <input
          type="checkbox"
          v-model="globalEnabled"
          @change="updateGlobalEnabled"
          class="logger-checkbox"
        />
        <span class="logger-toggle-label">Enable Logging</span>
      </label>
    </div>

    <!-- Namespace List -->
    <div class="logger-section">
      <h4 class="logger-subtitle">Namespaces</h4>

      <!-- Search Input -->
      <div class="logger-search">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search namespaces..."
          class="logger-search-input"
        />
      </div>

      <div class="logger-namespaces">
        <div v-if="filteredNamespaces.length > 0">
          <label
            v-for="namespace in filteredNamespaces"
            :key="namespace"
            class="logger-namespace-item"
          >
            <input
              type="checkbox"
              :checked="namespaceEnabled[namespace]"
              @change="
                (event) => toggleNamespace(namespace, (event.target as HTMLInputElement).checked)
              "
              class="logger-checkbox"
            />
            <span class="logger-namespace-label">{{ namespace }}</span>
          </label>
        </div>
        <div v-else-if="searchQuery" class="logger-no-results">
          No namespaces found matching "{{ searchQuery }}"
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="logger-actions">
      <button @click="enableAll" class="logger-action-btn">Enable All</button>
      <button @click="disableAll" class="logger-action-btn">Disable All</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { LoggerManager } from '@/utils/logger'

const globalEnabled = ref(false)
const namespaces = ref<string[]>([])
const namespaceEnabled = reactive<Record<string, boolean>>({})
const searchQuery = ref('')

// Computed property for filtered namespaces
const filteredNamespaces = computed(() => {
  return (
    searchQuery.value
      ? namespaces.value.filter((namespace) =>
          namespace.toLowerCase().includes(searchQuery.value.toLowerCase()),
        )
      : namespaces.value
  ).sort((a, b) => (a > b ? 1 : -1))
})

onMounted(() => {
  loadConfig()
})

function loadConfig() {
  const config = LoggerManager.getConfig()
  globalEnabled.value = config.enabled
  namespaces.value = LoggerManager.getAllNamespaces()

  // Update reactive object
  namespaces.value.forEach((namespace) => {
    namespaceEnabled[namespace] = config.namespaces[namespace] || false
  })
}

function updateGlobalEnabled() {
  LoggerManager.setGlobalEnabled(globalEnabled.value)
}

function toggleNamespace(namespace: string, enabled: boolean) {
  namespaceEnabled[namespace] = enabled
  LoggerManager.enableNamespace(namespace, enabled)
}

function enableAll() {
  // Enable all namespaces, not just filtered ones
  namespaces.value.forEach((namespace) => {
    namespaceEnabled[namespace] = true
    LoggerManager.enableNamespace(namespace, true)
  })
}

function disableAll() {
  // Disable all namespaces, not just filtered ones
  namespaces.value.forEach((namespace) => {
    namespaceEnabled[namespace] = false
    LoggerManager.enableNamespace(namespace, false)
  })
}

// Expose loadConfig for parent component to call when needed
defineExpose({
  loadConfig,
})
</script>

<style scoped>
.logger-config {
  width: 100%;
}

.logger-section {
  margin-bottom: 24px;
}

.logger-subtitle {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
}

.logger-toggle {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.logger-toggle-label {
  margin-left: 12px;
  font-size: 16px;
  font-weight: 500;
  color: #111827;
}

.logger-search {
  margin-bottom: 12px;
}

.logger-search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background: white;
  transition: border-color 0.2s ease;
}

.logger-search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.logger-namespaces {
  /* max-height: 60vh; */
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
}

.logger-no-results {
  text-align: center;
  padding: 20px;
  color: #6b7280;
  font-style: italic;
}

.logger-namespace-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.logger-namespace-item:hover {
  background-color: #f9fafb;
}

.logger-namespace-label {
  margin-left: 12px;
  font-size: 14px;
  color: #374151;
  font-family: 'Courier New', monospace;
}

.logger-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
}

.logger-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.logger-action-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.logger-action-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
</style>
